'use client';

import { useCallback, useMemo, type ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import { ContextNavigation, ListPageSearch } from '@terra/ui-design-system';
import {
  ASSET_TYPE,
  useTabScopedAssetsFilters,
} from '../../../store/tab/assetsTabScopedFilterStore';
import {
  useAssetCounts,
  useAssetsSegmentMetrics,
} from '../../../hooks/useAssetCounts';
import {
  buildVerifiedAssetsMetricsBody,
  type AssetsSearchRequestFilters,
} from '../../../hooks/api/assets-search-request';
import { ListPageRefreshButton } from '../../common/ListPageRefreshButton';
import {
  ASSETS_SEGMENTS_BY_VIEW,
  ASSETS_SEGMENT_FILTERS,
  ASSETS_VIEW_METRICS_TYPE,
  resolveAssetsSegmentCount,
  resolveSelectedAssetsSegment,
  toAssetsSegmentCounts,
  type AssetsSegment,
} from './assets-view.model';

type AssetsContextNavigationProps = {
  requestFilters: AssetsSearchRequestFilters;
  onRefresh: () => void;
  isLoading: boolean;
  optionsMenu: ReactNode;
};

export function AssetsContextNavigation({
  requestFilters,
  onRefresh,
  isLoading,
  optionsMenu,
}: AssetsContextNavigationProps) {
  const t = useTranslations('proxy.assets.chips');
  const tSearch = useTranslations('designSystem.common');
  const tAssetEntity = useTranslations('designSystem.signals.drawer');
  const {
    view,
    typeFilter,
    securityFilter,
    setTypeFilter,
    setEndpointSegment,
    searchTerm,
    setSearchTerm,
  } = useTabScopedAssetsFilters();
  const typeCounts = useAssetCounts();
  const metricsBody = useMemo(() => {
    const type = ASSETS_VIEW_METRICS_TYPE[view];

    return type
      ? buildVerifiedAssetsMetricsBody({ filters: requestFilters, type })
      : null;
  }, [requestFilters, view]);
  const metrics = useAssetsSegmentMetrics({ body: metricsBody });
  const counts = useMemo(
    () =>
      toAssetsSegmentCounts({
        view,
        metrics,
        unverifiedCount: typeCounts.isLoading
          ? null
          : typeCounts.unverifiedCount,
      }),
    [metrics, typeCounts.isLoading, typeCounts.unverifiedCount, view],
  );
  const items = useMemo(
    () =>
      ASSETS_SEGMENTS_BY_VIEW[view].map((segment) => ({
        value: segment,
        label: t(segment),
        count: resolveAssetsSegmentCount({ segment, view, counts }),
      })),
    [counts, t, view],
  );
  const handleSegmentChange = useCallback(
    (segment: AssetsSegment) => {
      const segmentFilters = ASSETS_SEGMENT_FILTERS[segment];

      if (segmentFilters.typeFilter !== ASSET_TYPE.ENDPOINTS) {
        setTypeFilter(segmentFilters.typeFilter);

        return;
      }

      setEndpointSegment(segmentFilters.securityFilter);
    },
    [setEndpointSegment, setTypeFilter],
  );

  return (
    <ContextNavigation
      ariaLabel={t('label')}
      items={items}
      value={resolveSelectedAssetsSegment({ view, typeFilter, securityFilter })}
      onValueChange={handleSegmentChange}
      actions={
        <>
          <ListPageSearch
            value={searchTerm}
            onValueChange={setSearchTerm}
            placeholder={tSearch('searchByNameOrId', {
              entityName: tAssetEntity('asset'),
            })}
          />
          <ListPageRefreshButton onRefresh={onRefresh} isLoading={isLoading} />
          {optionsMenu}
        </>
      }
    />
  );
}
